import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import './OAuthCallback.css';
import { getWorkspacePath } from '../../utils/workspaceUtils';
import { handleOAuthCallback } from '../../store/Integration';

// Global Set to track processed OAuth codes across all component instances
const globalProcessedCodes = new Set();

// Helper to format integration name for display
const formatDisplayName = (name) => {
  if (!name) return 'Integration';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

const OAuthCallback = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { provider } = useParams();

  // Resolve provider from URL params or path. Gmail/Outlook email OAuth is no longer supported,
  // so those branches are gone — only WhatsApp / Facebook / future providers route through here.
  const getProvider = () => {
    if (provider) return provider.toLowerCase();
    const path = location.pathname.toLowerCase();
    if (path.includes('whatsapp')) return 'whatsapp';
    if (path.includes('facebook')) return 'facebook';
    return 'unknown';
  }

  const integrationProvider = getProvider();
  const displayName = formatDisplayName(integrationProvider);

  // Redux state - use dynamic provider-specific callback state
  const { loading, email: reduxEmail, metadata: reduxMetadata, error } = useSelector(state => {
    return state['Integration']['oauthCallback']?.[integrationProvider] || {};
  })

  const processingRef = useRef(false);
  const redirectedRef = useRef(false);
  const { currentWorkspace } = useSelector(state => state['Settings']);

  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(true);

  // Handle Redux state changes (after backend callback completes)
  useEffect(() => {
    if (reduxEmail && !loading) {
      setEmail(reduxEmail);
      setIsProcessing(false);
      processingRef.current = false;
    } else if (error && !loading) {
      setErrorMessage(error);
      setIsProcessing(false);
      processingRef['current'] = false;
    }
  }, [reduxEmail, error, loading]);

  // Auto-redirect to sales accounts page when linked via metadata
  useEffect(() => {
    if (redirectedRef.current || !email) return;

    const urlParams = new URLSearchParams(location['search']);
    const salesAccountId = reduxMetadata?.salesAccountId || urlParams.get('salesAccountId');
    if (!salesAccountId) return;

    redirectedRef.current = true;
    setTimeout(() => {
      history.push(getWorkspacePath(`/connections/sales-accounts?account=${salesAccountId}`, currentWorkspace?.['_id']));
    }, 1500);
  }, [email, reduxMetadata, location, history, currentWorkspace]);

  // Process URL parameters on mount
  useEffect(() => {
    if (processingRef['current']) {
      console.log('OAuth callback already being processed, skipping...');
      return;
    }

    const urlParams = new URLSearchParams(location['search']);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const urlError = urlParams.get('error');
    const emailParam = urlParams.get('email');
    const phoneParam = urlParams.get('phone');
    const statusParam = urlParams.get('status');

    // Handle error in URL
    if (urlError) {
      setErrorMessage(`${displayName} connection failed: ${urlError}`);
      setIsProcessing(false);
      return;
    }

    // Handle success status from server redirect (with phone or email)
    if (statusParam === 'success' && (emailParam || phoneParam)) {
      const identifier = emailParam || phoneParam;
      setEmail(identifier);
      setIsProcessing(false);
      return;
    }

    // Handle error status from server redirect
    if (statusParam === 'error') {
      const errorParam = urlParams.get('error');
      setErrorMessage(errorParam ? decodeURIComponent(errorParam) : `${displayName} connection failed`);
      setIsProcessing(false);
      return;
    }

    // Handle email already in URL params
    if (emailParam) {
      setEmail(emailParam);
      setIsProcessing(false);
      return;
    }

    // Handle code + state (need to exchange with backend)
    if (code && state) {
      const cacheKey = `${code}:${state}`;
      if (globalProcessedCodes.has(cacheKey)) return;

      globalProcessedCodes.add(cacheKey);
      processingRef['current'] = true;
      setIsProcessing(true);

      // Dispatch to backend to exchange code for tokens
      dispatch(handleOAuthCallback(integrationProvider, code, state));

      // Clean up cache after 60 seconds
      setTimeout(() => globalProcessedCodes.delete(cacheKey), 60 * 1000);
      return;
    }

    // No valid parameters
    setErrorMessage('Invalid OAuth callback parameters');
    setIsProcessing(false);
  }, [location['search'], dispatch, displayName, integrationProvider]);

  const handleGoToDashboard = () => history.push(getWorkspacePath('/dashboard', currentWorkspace?.['_id']));
  const handleGoToIntegrations = () => history.push(getWorkspacePath('/connections/integrations', currentWorkspace?.['_id']));

  // Loading state
  if (isProcessing || loading) {
    return (
      <div className="oauth-success-container">
        <div className="oauth-success-content processing">
          <div className="loading-spinner"></div>
          <h1>Connecting {displayName}...</h1>
          <p>Please wait while we set up your {displayName} integration.</p>
        </div>
      </div>
    )
  }

  // Error state
  if (errorMessage)
    return (
      <div className="oauth-success-container">
        <div className="oauth-success-content">
          <div className="error-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1>{displayName} Connection Failed</h1>
          <p className="error-message">{errorMessage}</p>
          <div className="button-group">
            <button className="btn-primary" onClick={handleGoToIntegrations}>
              <span className="btn-icon">⚙️</span>
              Back to Integrations
            </button>
          </div>
        </div>
      </div>
    )

  // Success state
  return (
    <div className="oauth-success-container">
      <div className="oauth-success-content">
        <div className="success-icon">
          <div className="success-checkmark">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path stroke="white" strokeWidth="2" d="M20 6L9 17L4 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <h1>{displayName} Connected Successfully!</h1>
        <p>Your {displayName} integration has been configured and is ready to use.</p>

        {email && (
          <div className="email-badge">
            <span className="email-icon">{integrationProvider === 'whatsapp' ? '📱' : '📧'}</span>
            <strong>{email}</strong>
          </div>
        )}

        <p className="description">
          You can now use {displayName} tools in SellAgent for your workflows and automations.
        </p>

        <div className="button-group">
          <button className="btn-primary" onClick={handleGoToDashboard}>
            <span className="btn-icon">🏠</span>
            Go to Dashboard
          </button>
          <button className="btn-secondary" onClick={handleGoToIntegrations}>
            <span className="btn-icon">⚙️</span>
            View Integrations
          </button>
        </div>
      </div>
    </div>
  )
}

export default OAuthCallback